import { forwardRef } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';

/*
 * Thin Radix Dialog wrapper matched to the app's design tokens. Radix handles
 * focus trapping, ESC-to-close, scroll lock, and a11y labeling — we just
 * layer the surface, overlay, and close button on top.
 */

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogOverlay = forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Overlay>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
        ref={ref}
        className={cn(
            'fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px]',
            className,
        )}
        {...props}
    />
));
DialogOverlay.displayName = 'DialogOverlay';

interface DialogContentProps
    extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
    showClose?: boolean;
}

export const DialogContent = forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Content>,
    DialogContentProps
>(({ className, children, showClose = true, ...props }, ref) => (
    <DialogPrimitive.Portal>
        <DialogOverlay />
        <DialogPrimitive.Content
            ref={ref}
            className={cn(
                'fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                'w-[min(calc(100vw-2rem),32rem)] max-h-[calc(100vh-2rem)] overflow-auto',
                'bg-surface border border-border rounded-lg shadow-lg',
                'p-5',
                className,
            )}
            {...props}
        >
            {children}
            {showClose && (
                <DialogPrimitive.Close
                    className={cn(
                        'absolute top-3 right-3 p-1 rounded-md',
                        'text-text-muted hover:text-text hover:bg-surface-muted',
                        'focus:outline-none focus:ring-2 focus:ring-accent',
                        'transition-colors duration-[var(--duration-base)]',
                    )}
                    aria-label="Close"
                >
                    <X className="w-4 h-4" />
                </DialogPrimitive.Close>
            )}
        </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
));
DialogContent.displayName = 'DialogContent';

export const DialogTitle = forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Title>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Title
        ref={ref}
        className={cn('font-display text-lg font-bold text-text', className)}
        {...props}
    />
));
DialogTitle.displayName = 'DialogTitle';

export const DialogDescription = forwardRef<
    React.ComponentRef<typeof DialogPrimitive.Description>,
    React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
    <DialogPrimitive.Description
        ref={ref}
        className={cn('text-xs text-text-subtle mt-1', className)}
        {...props}
    />
));
DialogDescription.displayName = 'DialogDescription';

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    return <div className={cn('mb-4', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
    // Below sm:, buttons stack vertically full-width — three-button dialogs
    // (e.g. Cancel / Abandon & Leave / Save & Leave on the unsaved-changes
    // prompt) overflow the dialog when laid out as a single row on a phone.
    // Above sm:, fall back to the standard right-aligned desktop row with
    // intrinsic button widths.
    return (
        <div
            className={cn(
                'flex flex-col gap-2 mt-5 pt-4 border-t border-border',
                'sm:flex-row sm:items-center sm:justify-end',
                '[&>button]:w-full sm:[&>button]:w-auto',
                className,
            )}
            {...props}
        />
    );
}
